<template>
  <HighlightsWidget
    disableZeroState
    flexLayout
    fullLinkTitle="activity log"
    :isLoading="isLoading"
    showLinkToFullList
    title="Directory Activity"
    @navigateToFullList="handleActivityLogLinkClick"
  >
    <template v-slot:mainContent>
      <table
        :class="$style.table"
      >
        <TableHeader
          :columns="tableColumns"
        />
        <TableBody
          :columns="tableColumns"
          :inContainer="true"
          :isRefreshing="isLoading"
          :tableData="tableData"
        />
      </table>
    </template>
  </HighlightsWidget>
</template>

<script>
import { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import { formatMetrics } from 'client/components/Highlights/highlightsCommonUtils';
import { mapActions, mapState } from 'vuex';
import { quickPicksOptions } from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import EventMetadata, { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import EventTypes, { eventTypeNameMap } from 'client/Insights/ActivityLog/constants/EventTypes';
import HIGHLIGHTS_CONSTS from 'client/components/Highlights/constants/highlightsConsts';
import HighlightsWidget from 'client/components/Highlights/HighlightsWidget.vue';
import InsightsAccessMixin from 'client/Insights/InsightsAccessMixin';
import TableBody from 'components/Tables/TableBody.vue';
import TableHeader from 'components/Tables/TableHeader.vue';
import Util from 'client/util/Util';

const {
  EVENT_FIELD,
  STATUS_FIELD,
  TIMESTAMP_FIELD,
  ID_FIELD,
} = HIGHLIGHTS_CONSTS;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const systemUserModel = StoreModelMap[ItemTypeConsts.systemUser];

const DEFAULT_ACTIVITY_FIELDS = ['lastUserPortalAuth', 'lastLocationKnown', 'lastPasswordChange', 'lastStatusChange'];

export default {
  name: 'UserHighlightsEventSummary',

  components: {
    HighlightsWidget,
    TableBody,
    TableHeader,
  },

  mixins: [InsightsAccessMixin],

  data: () => ({
    isLoading: true,
    metrics: {
      lastDeviceAuth: {
        [ID_FIELD]: 'LastDeviceAuthentication',
        [EVENT_FIELD]: 'Last Device Authentication',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastLdapAuth: {
        [ID_FIELD]: 'LastLDAPAuthentication',
        [EVENT_FIELD]: 'Last LDAP Authentication',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastLocationKnown: {
        [ID_FIELD]: 'LastKnownLocation',
        [EVENT_FIELD]: 'Last Known Location',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastPasswordChange: {
        [ID_FIELD]: 'LastPasswordChange',
        [EVENT_FIELD]: 'Last Password Change',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastRadiusAuth: {
        [ID_FIELD]: 'LastRADIUSAuthentication',
        [EVENT_FIELD]: 'Last RADIUS Authentication',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastSsoAuth: {
        [ID_FIELD]: 'LastSSOAuthentication',
        [EVENT_FIELD]: 'Last SSO Authentication',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastStatusChange: {
        [ID_FIELD]: 'LastStatusChange',
        [EVENT_FIELD]: 'Last Status Change',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastUserPortalAuth: {
        [ID_FIELD]: 'LastUserPortalAuthentication',
        [EVENT_FIELD]: 'Last User Portal Authentication',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
    },
    tableColumns: [
      { name: 'Event', dataField: EVENT_FIELD },
      { name: 'Resource/Status', dataField: STATUS_FIELD },
      { name: 'Timestamp', dataField: TIMESTAMP_FIELD },
    ],
  }),
  computed: {
    ...mapState(systemUserModel, ['item']),

    tableData() {
      return formatMetrics(this.metrics, DEFAULT_ACTIVITY_FIELDS);
    },
  },

  created() {
    Promise.allSettled([
      this.getLastLdapAuth(),
      this.getLastLoginAttempt(),
      this.getLastPasswordChange(),
      this.getLastRadiusAuth(),
      this.getLastSsoAuth(),
      this.getLastStatusChange(),
      this.getLastUserPortalAuth(),
    ])
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'clearFilters',
      'setFilter',
      'setQuickPickDate',
    ]),

    getLastEventWithFilters(eventTypes) {
      return this.getLastEvent(
        eventTypes,
        [EventMetadata[eventFields.user].filterCriteria(this.item.username)],
      );
    },

    async getLastLdapAuth() {
      const { lastLdapAuth } = this.metrics;
      const event = await this.getLastEventWithFilters(EventTypes.ldapBind);
      if (event) {
        lastLdapAuth[STATUS_FIELD] = event.success ? 'successful' : 'unsuccessful';
        lastLdapAuth[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async getLastLoginAttempt() {
      const { lastDeviceAuth, lastLocationKnown } = this.metrics;
      const event = await this.getLastEventWithFilters(EventTypes.loginAttempt);
      if (event) {
        lastDeviceAuth[STATUS_FIELD] = event.success ? 'successful' : 'unsuccessful';
        lastDeviceAuth[TIMESTAMP_FIELD] = this.getTimestampValue(event);

        lastLocationKnown[STATUS_FIELD] = EventMetadata[eventFields.geoip].getDataValue(event);
        lastLocationKnown[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async getLastPasswordChange() {
      const { lastPasswordChange } = this.metrics;
      const event = await this.getLastEventWithFilters(EventTypes.userPasswordChange);
      if (event) {
        lastPasswordChange[STATUS_FIELD] = 'successful';
        lastPasswordChange[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async getLastRadiusAuth() {
      const { lastRadiusAuth } = this.metrics;
      const event = await this.getLastEventWithFilters(EventTypes.radiusAuthAttempt);
      if (event) {
        lastRadiusAuth[STATUS_FIELD] = event.success ? 'successful' : 'unsuccessful';
        lastRadiusAuth[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async getLastSsoAuth() {
      const { lastSsoAuth } = this.metrics;
      const event = await this.getLastEventWithFilters(EventTypes.ssoAuth);
      if (event) {
        lastSsoAuth[STATUS_FIELD] = event.sso_token_success || event.success ? 'successful' : 'unsuccessful';
        lastSsoAuth[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async getLastStatusChange() {
      const event = await this.getLastEventWithFilters([
        EventTypes.userActivated,
        EventTypes.userLockout,
        EventTypes.userPasswordExpired,
        EventTypes.userSuspended,
        EventTypes.userUnlocked,
      ]);
      if (event) {
        const { lastStatusChange } = this.metrics;
        const eventType = EventMetadata[eventFields.eventType].getDataValue(event);
        lastStatusChange[STATUS_FIELD] = eventTypeNameMap[eventType] || '';
        lastStatusChange[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async getLastUserPortalAuth() {
      const { lastUserPortalAuth } = this.metrics;
      const event = await this.getLastEventWithFilters(EventTypes.userLoginAttempt);
      if (event) {
        lastUserPortalAuth[STATUS_FIELD] = event.success ? 'successful' : 'failed';
        lastUserPortalAuth[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    getTimestampValue(event) {
      return EventMetadata[eventFields.timestamp].getDataValue(event);
    },

    handleActivityLogLinkClick() {
      // Clear any existing Activity Log filters
      this.clearFilters();

      // Set the time range to the first date available (15 or 90 days ago)
      const date = this.directoryInsightsPremium
        ? quickPicksOptions.ninetyDays
        : quickPicksOptions.fifteenDays;
      this.setQuickPickDate(date);

      // Set the "User" filter for the Activity Log. Username must be cast to lowercase
      // for the activity log filter to be applied.
      this.setFilter({
        field: eventFields.user,
        value: [this.item.username.toLowerCase()],
      });

      Util.redirect('#/insights');
    },
  },
};
</script>

<style module>
.table {
  text-align: left;
  width: 100%;
}
</style>
